import { Timestamp, DocumentSnapshot, DocumentData } from "firebase/firestore";
import { z } from "zod";
import { RewardType } from "./loyalty-program-template";
import { dropUndefined } from "../utils/dropUndefined";
import { guardIsPlainObject } from "../utils/isPlainObject";
import { NotFoundError } from "../error-classes";

export enum TransactionStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  REDEEMED = "redeemed",
  EXPIRED = "expired",
  CANCELLED = "cancelled",
}

export interface LoyaltyTransaction {
  // Program Reference
  loyaltyProgramID: string;

  // Transaction DetailsL
  customerID: string;

  // Source Transaction
  sourceInvoiceID: string;
  sourceCraftRecordID?: string;
  sourceAmount: number;

  // Reward Details
  rewardType: RewardType;
  rewardValue: number;
  pointsEarned?: number;
  discountAmount?: number;

  // Status & Lifecycle
  status: TransactionStatus;
  earnedDate: Timestamp;
  expirationDate?: Timestamp;

  // Processing Info
  processedBy?: string;
  processingNote?: string;

  // Audit fields (auto-populated)
  timestampCreated: Timestamp;
  timestampLastModified: Timestamp;
  createdBy: string;
  lastModifiedBy: string;
}

export interface ExistingLoyaltyTransaction extends LoyaltyTransaction {
  id: string;
  refPath: string;
  siteKey: string;
}

// Zod schemas for validation
export const LoyaltyTransactionSchema = z.object({
  loyaltyProgramID: z.string().min(1, "Loyalty program ID is required"),
  customerID: z.string().min(1, "Customer ID is required"),
  sourceInvoiceID: z.string().min(1, "Source invoice ID is required"),
  sourceCraftRecordID: z.string().optional(),
  sourceAmount: z.number().min(0, "Source amount must be positive"),
  rewardType: z.nativeEnum(RewardType),
  rewardValue: z.number().min(0, "Reward value must be positive"),
  pointsEarned: z.number().min(0).optional(),
  discountAmount: z.number().min(0).optional(),
  status: z.nativeEnum(TransactionStatus),
  earnedDate: z.instanceof(Timestamp),
  expirationDate: z.instanceof(Timestamp).optional(),
  processedBy: z.string().optional(),
  processingNote: z.string().max(500, "Processing note too long").optional(),
});

export type LoyaltyTransactionFormData = z.infer<
  typeof LoyaltyTransactionSchema
>;

// Helper functions
export function getReadableTransactionStatus(
  status: TransactionStatus,
): string {
  switch (status) {
    case TransactionStatus.PENDING:
      return "Pending";
    case TransactionStatus.APPROVED:
      return "Approved";
    case TransactionStatus.REJECTED:
      return "Rejected";
    case TransactionStatus.REDEEMED:
      return "Redeemed";
    case TransactionStatus.EXPIRED:
      return "Expired";
    case TransactionStatus.CANCELLED:
      return "Cancelled";
    default:
      return status;
  }
}

export function getTransactionStatusColor(status: TransactionStatus): string {
  switch (status) {
    case TransactionStatus.PENDING:
      return "text-yellow-600 bg-yellow-100";
    case TransactionStatus.APPROVED:
      return "text-green-600 bg-green-100";
    case TransactionStatus.REJECTED:
      return "text-red-600 bg-red-100";
    case TransactionStatus.REDEEMED:
      return "text-blue-600 bg-blue-100";
    case TransactionStatus.EXPIRED:
      return "text-gray-600 bg-gray-100";
    case TransactionStatus.CANCELLED:
      return "text-gray-600 bg-gray-100";
    default:
      return "text-gray-600 bg-gray-100";
  }
}

// API types - matching the backend interfaces
export interface APICreateLoyaltyTransactionRequest {
  siteKey: string;
  loyaltyProgramID: string;
  customerID: string;
  sourceInvoiceID: string;
  sourceAmount: number;
  rewardType:
    | "points"
    | "percentageDiscount"
    | "fixedDiscount"
    | "commission"
    | "freeService";
  rewardValuet: number;
  pointsEarned?: number;
  status: "pending" | "approved" | "processed" | "failed" | "cancelled";
  earnedDate: string; // ISO string
  expirationDate?: string; // ISO string
  processingNote?: string;
}

export interface APIUpdateLoyaltyTransactionRequest {
  siteKey: string;
  transactionID: string;
  loyaltyProgramID?: string;
  customerID?: string;
  sourceInvoiceID?: string;
  sourceAmount?: number;
  rewardType?:
    | "points"
    | "percentageDiscount"
    | "fixedDiscount"
    | "commission"
    | "freeService";
  rewardValue?: number;
  pointsEarned?: number;
  status?: "pending" | "approved" | "processed" | "failed" | "cancelled";
  expirationDate?: string; // ISO string
  processingNote?: string;
  externalTransactionID?: string;
}

export interface APIApproveLoyaltyTransactionRequest {
  siteKey: string;
  transactionID: string;
  processingNote?: string;
  sendEmail?: boolean;
}

export interface APIRejectLoyaltyTransactionRequest {
  siteKey: string;
  transactionID: string;
  processingNote?: string;
}

export interface APIGetLoyaltyTransactionsRequest {
  siteKey: string;
  status?: "pending" | "approved" | "processed" | "failed" | "cancelled";
  customerID?: string;
  loyaltyProgramID?: string;
  page?: number;
  limit?: number;
}

export interface APIGetLoyaltyTransactionRequest {
  siteKey: string;
  transactionID: string;
}

export interface APIGetPendingLoyaltyTransactionsRequest {
  siteKey: string;
}

export interface APIRecalculateLoyaltyRequest {
  siteKey: string;
  invoiceID?: string;
  craftRecordID?: string;
}

export interface APIRecalculateLoyaltyResponse {
  message: string;
  transactionsCreated: number;
  transactionsUpdated: number;
  invoicesProcessed: string[];
  skippedInvoices: string[];
}

// Interface for API responses
export interface PaginatedTransactionsResponse {
  transactions: ExistingLoyaltyTransaction[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Manager class
export const LoyaltyTransactionManager = {
  convertForFirestore: convertForFirestore,
  createFromSnapshot: createFromSnapshot,
  parse: validateLoyaltyTransaction,
} as const;

function convertForFirestore(
  transaction: ExistingLoyaltyTransaction | APIUpdateLoyaltyTransactionRequest,
): DocumentData {
  const local = Object.assign({}, transaction);
  // Remove metadata fields that shouldn't be sent to Firestore
  delete (local as any).id;
  delete (local as any).refPath;
  delete (local as any).siteKey;
  delete (local as any).transactionID;
  return dropUndefined(local);
}

function createFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingLoyaltyTransaction {
  if (!snapshot.exists()) {
    throw new NotFoundError("Document does not exist.");
  }

  const siteKey = snapshot.ref.path.split("/")[1]; // Extract siteKey from path

  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    siteKey,
    ...validateLoyaltyTransaction(snapshot.data()),
  };
}

function validateLoyaltyTransaction(value: unknown): LoyaltyTransaction {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }

  // Extend the schema to include audit fields for validation
  const fullSchema = LoyaltyTransactionSchema.extend({
    timestampCreated: z.instanceof(Timestamp),
    timestampLastModified: z.instanceof(Timestamp),
    createdBy: z.string(),
    lastModifiedBy: z.string(),
  });

  return fullSchema.parse(value);
}
